import React, { useState } from "react";
import { FaChevronDown, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaQuestionCircle } from "react-icons/fa";
// import { HiOutlineMail, HiOutlineUser } from "react-icons/hi";
// import { MdMessage } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";

const faqs = [
    {
      question: "What services does your company provide?",
      answer:
        "We provide AI, Blockchain, Web3, Web2, Mobile App, Website, Cloud, and Custom Software development services. Our team builds secure, scalable, and high-performance solutions tailored to client requirements for startups, businesses, and enterprise-level projects.",
    },
    {
      question: "How long does a project take?",
      answer:
        "Project timelines depend on complexity, but most standard projects are completed within 1–2 weeks. Larger or enterprise-level systems may take more time. We always provide a clear timeline before starting and keep clients updated during development.",
    },
    {
      question: "Do you provide post-launch support?",
      answer:
        "Yes, we provide full post-launch support including maintenance, updates, bug fixes, performance optimization, and security improvements to ensure your website, app, or blockchain system runs smoothly.",
    },
    {
      question: "Which technologies do you work with?",
      answer:
        "We specialize in AI, Blockchain, Web3, Web2, Mobile Apps, Websites, and Cloud solutions. However, we are not limited to specific technologies. We can work with any technology stack based on client requirements to build custom, scalable, and high-quality software solutions.",
    },
    {
      question: "Can you build custom projects based on client requirements?",
      answer:
        "Yes, we can build fully custom solutions based on your needs including websites, mobile apps, blockchain platforms, MLM systems, AI tools, cloud systems, and enterprise software. If you need any specific technology, our team can work on it.",
    },
  ];

function FAQSection() {
  const [active, setActive] = useState(null);

  const toggleFAQ = (index) => {
    setActive(active === index ? null : index);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  return (
    <>
      {/* FAQ Section */}
      <section className="bg-gradient-to-br from-[#fafafa] to-[#f0f3f1] py-20 sm:py-24 md:py-28 px-3 sm:px-4 relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-[#12AF09]/10 to-transparent rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#12AF09]/10 to-transparent rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10">


          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-center mb-12 sm:mb-16"
          >
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-gray-900 mb-4 tracking-tight">
              Frequently Asked
              <span className="block font-bold text-[#12AF09] mt-2 relative">
                Questions
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-transparent via-[#12AF09] to-transparent rounded-full"></div>
              </span>
            </h2>

            <p className="text-gray-600 mt-6 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
              Everything you need to know about our blockchain services. 
              Can't find what you're looking for? Our team is always here to help.
            </p>
          </motion.div>

          {/* FAQ */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-4 sm:space-y-5"
          >
            {faqs.map((faq, index) => {
              const isOpen = active === index;

              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  layout
                  transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                  className="group"
                >
                  <div
                    className={`bg-white rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-500 ${
                      isOpen 
                        ? "shadow-2xl scale-100" 
                        : "shadow-lg hover:shadow-xl scale-100 hover:scale-[1.02]"
                    }`}
                    style={{
                      boxShadow: isOpen 
                        ? "0 25px 50px -12px rgba(38, 189, 25, 0.25)" 
                        : "0 10px 30px -15px rgba(0, 0, 0, 0.1)"
                    }}
                  >
                    {/* Question */}
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full flex justify-between items-center p-4 sm:p-7 text-left cursor-pointer relative"
                    >
                      <div className="flex items-start gap-4 sm:gap-5 flex-1 pr-3">
                        <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                          isOpen 
                            ? "from-[#12AF09] to-[#1a8a12] shadow-lg shadow-[#12AF09]/30" 
                            : "from-green-100 to-green-50 group-hover:from-[#12AF09]/10 group-hover:to-green-50"
                        }`}>
                          <FaQuestionCircle className={`text-lg sm:text-xl transition-all duration-500 ${
                            isOpen ? "text-white" : "text-[#12AF09]"
                          }`} />
                        </div>
                        <span className={`font-medium text-base sm:text-lg md:text-xl leading-relaxed transition-colors duration-500 ${
                          isOpen ? "text-[#12AF09]" : "text-gray-800"
                        }`}>
                          {faq.question}
                        </span>
                      </div>

                      <motion.div
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
                          isOpen 
                            ? "bg-[#12AF09] text-white shadow-lg shadow-[#12AF09]/30" 
                            : "bg-gray-100 text-gray-400 group-hover:bg-[#12AF09] group-hover:text-white"
                        }`}
                      >
                        <FaChevronDown className="text-sm sm:text-base" />
                      </motion.div>

                      {/* Decorative corner accent */}
                      <div className={`absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-[#12AF09]/10 to-transparent rounded-tl-2xl transition-opacity duration-500 ${
                        isOpen ? "opacity-100" : "opacity-0"
                      }`}></div>
                    </button>

                    {/* Answer */}
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          layout
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        >
                          <div className="px-5 sm:px-7 pb-5 sm:pb-7 pl-[4.5rem] sm:pl-20">
                            <div className="border-t-2 border-[#12AF09]/20 pt-5 sm:pt-6">
                              <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed">
                                {faq.answer}
                              </p>
                              
                              {/* Read more link */}
                              <button className="mt-4 text-[#12AF09] font-medium text-sm sm:text-base hover:text-[#1a8a12] inline-flex items-center gap-2 group/btn transition-all duration-300">
                                <span>Learn more about this service</span>
                                <FaChevronDown className="text-xs rotate-[-90deg] group-hover/btn:translate-x-1 transition-transform duration-300" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>
      </section>

      {/* Contact & Map Section */}
      <section className="bg-gradient-to-b from-[#02140d] to-[#000000]  py-10 sm:py-10 md:py-20 px-3 sm:px-4 relative overflow-hidden">
        {/* Premium decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-black/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-20 bg-gradient-to-t from-black/50 to-transparent"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#12AF09]/5 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            {/* <span className="inline-block px-5 py-2 mb-4 text-xs font-semibold tracking-wider text-[#12AF09] uppercase bg-[#12AF09]/10 backdrop-blur-sm rounded-full border border-[#12AF09]/20">
              Visit Us
            </span> */}
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-4 tracking-tight">
              Find Our
              <span className="font-bold text-[#12AF09] mt-2">
               {" "} Location
              </span>
            </h2>
            <p className="text-gray-400 mt-4 text-base sm:text-lg max-w-2xl mx-auto">
              Visit our headquarters or get in touch with our team for a personal consultation
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Contact Information Cards */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* Office Hours Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10 hover:border-[#12AF09]/30 transition-all duration-500 group">
                <div className="flex items-start gap-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#12AF09] to-[#1a8a12] flex items-center justify-center shadow-lg shadow-[#12AF09]/30 group-hover:scale-110 transition-transform duration-500">
                    <FaClock className="text-2xl text-white" />
                  </div>
                  <div>
                    <h3 className="text-white text-xl font-semibold mb-3">Office Hours</h3>
                    <div className="space-y-2 text-gray-300">
                      <p className="flex items-center gap-2">
                        <span className="w-40 text-gray-400">Monday - Saturday:</span>
                        <span className="font-medium">10:00 AM - 6:00 PM</span>
                      </p>
                  
                      <p className="flex items-center gap-2">
                        <span className="w-40 text-gray-400">Sunday:</span>
                        <span className="font-medium text-[#12AF09]">Closed</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 border border-white/10 hover:border-[#12AF09]/30 transition-all duration-500 group">
                <h3 className="text-white text-xl font-semibold mb-6">Contact Directly</h3>
                <div className="space-y-5">
                  <motion.div 
                    whileHover={{ x: 10 }}
                    className="flex items-center gap-4 text-gray-300 group/item cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#12AF09]/10 flex items-center justify-center group-hover/item:bg-[#12AF09] transition-all duration-300">
                      <FaPhoneAlt className="text-[#12AF09] group-hover/item:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Phone</p>
                      <p className="text-white font-medium">+91 63935 61379</p>
                    </div>
                  </motion.div>

                  <motion.div 
                    whileHover={{ x: 10 }}
                    className="flex items-center gap-4 text-gray-300 group/item cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#12AF09]/10 flex items-center justify-center group-hover/item:bg-[#12AF09] transition-all duration-300">
                      <FaEnvelope className="text-[#12AF09] group-hover/item:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="text-white font-medium">Connect@chainmagicianlab.com</p>
                    </div>
                  </motion.div>

                  <motion.div 
                    whileHover={{ x: 10 }}
                    className="flex items-start gap-4 text-gray-300 group/item cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#12AF09]/10 flex items-center justify-center group-hover/item:bg-[#12AF09] transition-all duration-300 shrink-0">
                      <FaMapMarkerAlt className="text-[#12AF09] group-hover/item:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Address</p>
                      <p className="text-white font-medium leading-relaxed">
                        Chain Magician Lab Pvt Ltd
                        <br />
                        Shantipuram, Phaphamau
                        <br />
                        Uttar Pradesh, India 211013
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>

            </motion.div>

            {/* Map Container */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-3"
            >
              <div className="relative group">
                {/* Premium map frame */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#12AF09] to-[#1a8a12] rounded-3xl opacity-20 group-hover:opacity-40 blur transition-all duration-500"></div>
                
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <iframe
                  title="company directions"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28802.724715868815!2d81.82969059166169!3d25.527029497173036!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399acb00548ee2f3%3A0xdad2e57008b2a8a0!2sChain%20magician%20Lab%20blockchain%20company!5e0!3m2!1sen!2sin!4v1773470989457!5m2!1sen!2sin"
                    width="100%"
                    height="500"
                    style={{ border: 0, filter: "grayscale(20%)" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-[400px] sm:h-[450px] md:h-[500px]"
                  ></iframe>

                  {/* Map overlay with location info */}
                  <div className="absolute bottom-2 md:bottom-6 left-2 md:left-6 right-2 md:right-6 bg-black/80 backdrop-blur-md rounded-xl p-4 border border-[#12AF09]/30">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#12AF09] flex items-center justify-center animate-pulse">
                        <FaMapMarkerAlt className="text-white" />
                      </div>
                      <div>
                        <p className="text-white font-semibold">Chain Magician Lab</p>
                        <p className="text-gray-300 text-sm">Shantipuram, Phaphamau, Uttar Pradesh 211013</p>
                      </div>
                      <a
                        href="https://maps.app.goo.gl/35YC8eJfYvqHkApNA"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto px-4 py-2 bg-[#12AF09] text-white rounded-lg text-sm font-medium hover:bg-[#1a8a12] transition-colors duration-300"
                      >
                        Get Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}

export default FAQSection;